import moment from 'moment-timezone';

import { FREQUENCIES, UNITS, convertSource, UNITS_EXCEPTIONS } from '../constants/econdata';

// FredOptions
export function findSeriesFrequencyIdx(series) {
  return findFrequencyIdx(series.frequency_short);
}

// FredOptions
export function findFirstValidFrequencyIdx(fredSeries) {
  if (!fredSeries.length) return 0;
  const freqIndexes = fredSeries.map(findSeriesFrequencyIdx);
  return Math.max(...freqIndexes);
}

/* creates a function to find a specific action in a hash of props
 *
 * const getAction = createUpdateActions({
 *  updateFredEnd,
 *  updateFredFrequency,
 *  updateFredMethod,
 * });
 *
 * const action = getAction("Frequency")
 * action()
 */
export function createUpdateActions(actions) {
  return function (prop) {
    return actions[`updateFred${prop}`];
  };
}

export function createValidFrequencies(index) {
  return [{ code: '', text: 'Default' }, ...FREQUENCIES.slice(index)];
}

export function createValidUnits(allUnitsValid) {
  return allUnitsValid ? UNITS : UNITS.slice(1, -1);
}

export function findDates(updatedSeries) {
  return ['start', 'end'].reduce(
    (obj, prop) => Object.assign(obj, createDateObj(updatedSeries, prop)),
    {}
  );
}

export function findFrequency(currentFrequency, newFrequency) {
  const indexes = [currentFrequency, newFrequency].map(findFrequencyIdx);
  const validIdx = Math.max(...indexes);
  return FREQUENCIES[validIdx].code;
}

/**
 * Checks units for exceptions, if two units are not worded same way, they still can refer to the same unit,
 * see UNITS_EXCEPTIONS constant for a full list.
 * @export
 * @param {String} unit1 - unit for the first series
 * @param {String} unit2 - unit for the new series
 * @return {Boolean}
 */
export function checkForExceptions(unit1, unit2) {
  return Object.keys(UNITS_EXCEPTIONS).some((exception) => {
    const choices = UNITS_EXCEPTIONS[exception];
    return choices.includes(unit1) && choices.includes(unit2);
  });
}

export function isInvalidUnits(existingSeries, newSeries) {
  if (existingSeries[0].units_short === newSeries.units_short) return true;
  return checkForExceptions(existingSeries[0].units_short, newSeries.units_short);
}

// chart-data-actions
// queryFred
export function buildFredSources(sources) {
  const s = sources.length > 1 ? 's' : '';
  const sourceText = sources
    .reduce((str, source) => str + `${convertSource(source)}, `, '')
    .slice(0, -2);
  return `Source${s}: ${sourceText} via St. Louis Fed`;
}

export function getPrefixSuffix(defaultUnits, unitsType) {
  const unitsSymbol = UNITS.find((item) => item.code === unitsType).suffix;
  if (unitsSymbol) return ['', '%'];
  const suffix = defaultUnits
    .replace('Percent', '%')
    .replace('Millions', 'million')
    .replace('Billions', 'billion')
    .replace('Thousands', 'thousand');
  if (suffix.includes('Dollars')) {
    return [
      '$',
      ` ${suffix
        .replace(' of US Dollars', '')
        .replace(' of Dollars', '')
        .replace('Dollars', '')
        .toLowerCase()}`,
    ];
  }
  return ['', `${checkSpace(suffix)}${suffix.toLowerCase()}`];
}

export function getFredNote(seasonalAdjustment, moving, isMultiple) {
  const isSA = seasonalAdjustment === 'Seasonally Adjusted';
  return createNoteText(isSA, moving, isMultiple);
}

function checkSpace(suffix) {
  return suffix[0] === '%' ? '' : ' ';
}

// getBLS
export function createErrorMessage(messages) {
  /* if data unavailable for some of requested years,
  api returns an array of error mesages,
  which should be converted into a single string */
  if (messages.length) {
    const words = messages[0].split(' ');
    const isInvalid = words.slice(0, 2).join(' ') === 'Invalid Series';
    if (isInvalid) return `${words[4]} is not a valid series`;
  }
  const messageObj = buildMessageObj(messages);
  const messageStr = buildMessageStr(messageObj);
  return messageStr ? `Data unavailable for ${messageStr}.` : '';
}

export function checkData(series) {
  return series.data.length;
}

export function getMovingValue(moving, calculationsType) {
  return isNaN(+moving) || calculationsType === 'pct' ? 'none' : moving;
}

export function parseBLSData(allSeries, calculationsType, months, moving) {
  const dateObj = buildDatesObj(allSeries, 'bls');
  const prelim = {};
  const series = buildBLSSeries(allSeries, dateObj, prelim, calculationsType, months, moving);
  const [filteredSeries, categories] = createSeriesCategories(series);
  return [filteredSeries, categories, prelim];
}

export function getBLSFrequency(allSeries) {
  const firstData = allSeries.find(checkData);
  return checkQuarterly(firstData.data[0].periodName) ? 'q' : null;
}

export function buildBLSNote(prelimObj, ids, moving) {
  /* some observations for certain series are preliminary;
  parse data to create a note indicating which months are preliminary */
  const notesObj = buildNotesObj(prelimObj);
  return createFredNoteText(notesObj, ids, moving);
}

export function getBLSSuffix(calculationsType, suffix) {
  if (calculationsType === 'pct') return '%';
  if (suffix) return suffix;
}

// importData
export function getName(econType, allSeries, symbol, seriesObj) {
  return econType === 'FRED' ? getFredName(allSeries, symbol) : getBLSName(seriesObj, symbol);
}

// export to server
export function buildDatesObj(allSeries, type) {
  const dateObj = {};
  allSeries.forEach((series) =>
    series.data.forEach((point) => {
      const action = type === 'bls' ? createBLSDate : createFredDate;
      const date = action(point);
      dateObj[date] = null;
    })
  );
  return dateObj;
}

export function createFredDate(point) {
  const { date } = point;
  const year = date.slice(0, 4);
  const monthDay = date.slice(5).replace('-', '/');
  return `${monthDay}/${year}`;
}

export function createSeriesCategories(allSeries) {
  const series = removeLeadingNullPoints([...allSeries]);
  const categories = createCategories(series);
  return [series, categories];
}

export function checkValue(value) {
  return isNaN(value) ? null : value;
}

export function compileData(dataObj, moving) {
  const dataArr = buildDataArr(dataObj);
  const sortedData = sortData(dataArr);
  const movingValue = +moving;
  return isNaN(movingValue) ? sortedData : findMovingAverages(sortedData, movingValue);
}

// compileData helpers
function buildDataArr(seriesData) {
  return Object.keys(seriesData).map((key) => {
    return { x: key, y: seriesData[key] };
  });
}

function sortData(data) {
  return [...data].sort((a, b) =>
    moment(a.x, 'MM/DD/YYYY').isBefore(moment(b.x, 'MM/DD/YYYY')) ? -1 : 1
  );
}

export function findMovingAverages(data, moving) {
  return data
    .map((point, i) => {
      const previousPoints = createPreviousPoints(data, i, moving);
      const hasAllPreviousValues = previousPoints.length === moving - 1;
      if (hasAllPreviousValues) {
        const { x, y } = point;
        const sum = addValues(y, previousPoints);
        const average = sum / moving;
        return { x, y: average };
      }
    })
    .filter((item) => item);
}

function createPreviousPoints(data, idx, moving) {
  const points = [];
  for (let prevIdx = idx - 1; prevIdx > idx - moving; prevIdx--) {
    const point = data[prevIdx];
    const hasValue = point && !isNaN(+point.y);
    if (hasValue) points.push(point);
  }
  return points;
}

function addValues(currentY, previousPoints) {
  return previousPoints.reduce((sum, point) => sum + point.y, currentY);
}

// findSeriesFrequencyIdx helper
function findFrequencyIdx(frequencyCode) {
  return FREQUENCIES.findIndex((item) => item.code.toUpperCase() === frequencyCode);
}

// createErrorMessage helpers
function buildMessageObj(messages) {
  // compile unavailable years for each series
  const messageObj = {};
  messages.forEach((str) => {
    const words = str.split(' ');
    if (checkMessageType(words)) {
      const seriesName = words[5];
      const year = words[7];
      messageObj[seriesName]
        ? messageObj[seriesName].push(year)
        : (messageObj[seriesName] = [year]);
    }
  });
  return messageObj;
}

function checkMessageType(words) {
  // confirm whether error message is for no data available
  return words.slice(0, 3).join(' ') === 'No Data Available';
}

function buildMessageStr(messageObj) {
  // convert object to properly formatted string
  return Object.keys(messageObj)
    .reduce((str, seriesName) => {
      const yearsArr = messageObj[seriesName];
      const years = yearsArr.length > 1 ? getYears(yearsArr) : yearsArr[0];
      return str + `${seriesName} for ${years} and `;
    }, '')
    .slice(0, -5);
}

function getYears(years) {
  let [firstYear, lastYear] = ['min', 'max'].map((type) => getYear(years, type));
  const sameCentury = checkYears(firstYear, lastYear);
  // years should be in format 2000-05 or 1999-2005
  if (sameCentury) lastYear = lastYear.slice(2);
  return `${firstYear}-${lastYear}`;
}

function getYear(years, type) {
  return Math[type](...years).toString();
}

function checkYears(firstYear, lastYear) {
  return getCentury(firstYear) === getCentury(lastYear);
}

function getCentury(year) {
  return year.slice(0, 2);
}

// buildPrelimNote helpers
function buildNotesObj(prelimObj) {
  return Object.keys(prelimObj).reduce((obj, id) => {
    const noteStr = buildNoteStr(prelimObj[id]);
    return Object.assign(obj, { [id]: noteStr });
  }, {});
}

function buildNoteStr(years) {
  return Object.keys(years)
    .reduce((str, year) => {
      const months = years[year];
      const sortedMonths = sortMonths(months);
      const monthsStr = sortedMonths.join(' and ');
      const dateStr = `${monthsStr} ${year} and `;
      return str + dateStr;
    }, '')
    .slice(0, -5);
}

function sortMonths(months) {
  return [...months].sort((a, b) => (moment().month(a).isBefore(moment().month(b)) ? -1 : 1));
}

function createFredNoteText(notesObj, ids, moving) {
  const idsArr = Object.keys(notesObj);
  const firstNote = findFirstNote(notesObj, idsArr);
  const notesMatch = idsArr.every((note) => notesObj[note] === firstNote);
  const numIds = ids.length;
  const idsArrLength = idsArr.length;
  const noteForEveryId = idsArrLength === numIds;
  const allSeasonallyAdjusted = ids.every((id) => id[2] === 'S');
  const isMultiple = numIds > 1;
  let note = createNoteText(allSeasonallyAdjusted, moving, isMultiple);
  if (idsArrLength) {
    if (notesMatch) {
      const prelimStr = noteForEveryId ? ' F' : createPrelimStr(idsArr);
      note += `${prelimStr}igures for ${firstNote} are preliminary.`;
    } else {
      note += createMultipleNotes(idsArr, notesObj);
    }
  }
  return note;
}

function findFirstNote(notesObj, idsArr) {
  const firstId = idsArr[0];
  return notesObj[firstId];
}

function createNoteText(isSA, moving, isMultiple) {
  let noteText = 'Note:';
  if (isSA) noteText += ' Seasonally adjusted.';
  if (moving !== 'none') {
    const plural = isMultiple ? 's' : '';
    noteText += ` ${moving}-month moving average${plural}.`;
  }
  return noteText;
}

function createPrelimStr(idsArr) {
  const idsStr = idsArr.join(' and ');
  const description = idsStr === 'CES0500000011 and CES0500000003' ? 'Wage' : idsStr;
  return ` ${description} f`;
}

function createMultipleNotes(idsArr, notesObj) {
  return idsArr.reduce(
    (str, id) => str + ` ${id} figures for ${notesObj[id]} are preliminary.`,
    ''
  );
}

// findDates helpers
function createDateObj(updatedSeries, prop) {
  const value = findDate(updatedSeries, prop);
  return { [prop]: value };
}

function findDate(updatedSeries, prop) {
  const dates = updatedSeries.map((currentSeries) => {
    const date = currentSeries[`observation_${prop}`];
    return moment(date);
  });
  const comparison = prop === 'start' ? 'min' : 'max';
  const selectedDate = moment[comparison](dates);
  return selectedDate.format('YYYY-MM-DD');
}

// parseBLSData helpers
function createBLSDate(point) {
  const { year, periodName } = point;
  const month = getMonth(periodName);
  return `${month}/01/${year}`;
}

function getMonth(periodName) {
  if (periodName === 'Annual') return '01'; // annual

  const monthValue = getMonthValue(periodName);
  return moment().month(monthValue).format('MM');
}

function getMonthValue(periodName) {
  return checkQuarterly(periodName)
    ? getQuarterly(periodName) // quarterly
    : periodName; // monthly
}

function checkQuarterly(periodName) {
  return periodName.includes('Quarter');
}

function getQuarterly(periodName) {
  const quarter = +periodName[0];
  return (quarter - 1) * 3;
}

function buildBLSSeries(allSeries, dateObj, prelim, calculationsType, months, moving) {
  return allSeries.map((currentSeries) => {
    const seriesData = Object.assign({}, dateObj);
    const { data: currentData, seriesID } = currentSeries;
    currentData.forEach((point) => {
      updatePrelim(point, prelim, seriesID);
      const date = createBLSDate(point);
      const y = getY(point, calculationsType, months);
      seriesData[date] = y;
    });
    const data = compileData(seriesData, moving);
    return { data, symbol: seriesID };
  });
}

function updatePrelim(point, prelim, seriesId) {
  const { periodName, footnotes, year } = point;
  if (footnotes[0].code === 'P') {
    const seriesObj = prelim[seriesId];
    if (seriesObj) {
      seriesObj[year] ? seriesObj[year].push(periodName) : (seriesObj[year] = [periodName]);
    } else prelim[seriesId] = { [year]: [periodName] };
  }
}

function getY(point, calculationsType, months) {
  const { value, calculations } = point;
  const y =
    calculationsType === 'none' ? value : getCalculations(calculations, calculationsType, months);
  return checkValue(+y);
}

function getCalculations(calculations, calculationsType, months) {
  if (calculations) {
    const changeType = `${calculationsType}_changes`;
    return calculations[changeType][months];
  }
}

// createSeriesCategories helpers
function removeLeadingNullPoints(series) {
  const firstValues = series.map(findFirstValues);
  let earliestIdx;
  const emptySeries = [];
  firstValues.forEach((value, i) => {
    value === -1 ? emptySeries.push(i) : (earliestIdx = getEarliestIdx(earliestIdx, value));
  });
  const filteredSeries = filterSeries(series, emptySeries);
  filteredSeries.forEach(
    (currentSeries) => (currentSeries.data = currentSeries.data.slice(earliestIdx))
  );
  return filteredSeries;
}

function findFirstValues(series) {
  return series.data.findIndex((point) => point.y !== null);
}

function getEarliestIdx(earliestIdx, value) {
  return earliestIdx === undefined ? value : Math.min(value, earliestIdx);
}

function filterSeries(series, emptySeries) {
  return series.filter((currentSeries, i) => !emptySeries.includes(i));
}

function createCategories(series) {
  return series[0].data.map((point) => point.x);
}

// getName helpers
function getFredName(allSeries, symbol) {
  return allSeries.find((currentSeries) => currentSeries.id === symbol).title;
}

function getBLSName(seriesObj, symbol) {
  return seriesObj ? seriesObj[symbol] : symbol;
}
